import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "utils/api";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { useRouteTraversalModeV3 } from "../useRouteTraversalModeV3";
import { useRoutesCreationModeOrchestration } from "../useRoutesCreationModeOrchestration";
import { useRoutePathEditing } from "../useRoutePathEditing";
import { findRouteGroupByRouteId } from "../../utils/findRouteGroupByRouteId";
import { isPlaceholderGroupName } from "../../utils/isPlaceholderGroupName";
import { selectedRouteGroupAfterHistory } from "../../HistoryDrawer/utils/selectedGroupAfterHistory";
import { ALL_ROUTES_GROUP_LABEL } from "./constants";
import type { ScaledRoute } from "../../../SmartSurveyorForRudolfFritz/utils/types";
import type {
  ConfidenceLevel,
  LvMatch,
  PlanRouteGroupV3,
} from "../../utils/types";
import type { RenameRouteGroups } from "../../types";
import type { RenameSubmitAction } from "../../ApplyRenameToProjectModal";
import type { FollowHistoryChangesArgs } from "../../HistoryDrawer/utils/selectedGroupAfterHistory";
import type {
  CommitRouteRenameArgs,
  PendingRouteRename,
  UseRouteGroupSelectionV3Args,
} from "./types";

const EMPTY_ROUTES: ScaledRoute[] = [];

export const useRouteGroupSelectionV3 = ({
  canEdit,
  selectedPlan,
  routeGroups,
  floorPlanId,
  onDeleteRoute,
  onDeleteRouteGroup,
  onRenameRouteGroups,
  onMarkRouteTypeReviewed,
  onSetCableTypeNameStatus,
  onSetRouteGroupLvMatch,
  onUpdateRoute,
  onUpdateRouteGeometry,
  onBulkCreateRoutes,
  floorPlanPanelHandlers,
  isCableGroupApproved,
  toggleCableGroupApproval,
  interaction,
  traversing,
}: UseRouteGroupSelectionV3Args) => {
  const { t } = useTranslation();
  const [pendingRouteRename, setPendingRouteRename] =
    useState<PendingRouteRename | null>(null);
  const [routeRenameSubmitAction, setRouteRenameSubmitAction] =
    useState<RenameSubmitAction | null>(null);

  const prevFloorPlanIdRef = useRef(floorPlanId);

  const [selectedGroupLabel, setSelectedGroupLabel] = useState<string | null>(
    null,
  );
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);

  const isAllRoutesMode = selectedGroupLabel === ALL_ROUTES_GROUP_LABEL;

  const planRouteTypeIds = useMemo(
    () => routeGroups.map((group) => group.route_type_id),
    [routeGroups],
  );

  const totalCableMeters = useMemo(
    () => routeGroups.reduce((sum, group) => sum + group.meters, 0),
    [routeGroups],
  );

  const routeGroupLabels = useMemo(
    () => routeGroups.map((group) => group.label),
    [routeGroups],
  );

  const byGroupLabel = useMemo(() => {
    const result: Record<string, PlanRouteGroupV3> = {};
    for (const group of routeGroups) {
      result[group.label] = group;
    }
    return result;
  }, [routeGroups]);

  const allPlanRoutes = useMemo(
    () => routeGroups.flatMap((group) => group.routes),
    [routeGroups],
  );

  const selectedGroup = useMemo(
    () =>
      routeGroups.find((group) => group.label === selectedGroupLabel) ?? null,
    [routeGroups, selectedGroupLabel],
  );

  const selectedGroupRoutes = useMemo(() => {
    if (isAllRoutesMode) return allPlanRoutes;
    return selectedGroup?.routes ?? EMPTY_ROUTES;
  }, [isAllRoutesMode, allPlanRoutes, selectedGroup]);

  const totalSelectedGroupRoutes = useMemo(
    () => routeGroups.reduce((sum, group) => sum + group.routes.length, 0),
    [routeGroups],
  );

  const selectedRoute = useMemo(
    () =>
      selectedGroupRoutes.find((route) => route.id === selectedRouteId) ?? null,
    [selectedGroupRoutes, selectedRouteId],
  );

  const currentRouteTypeId = selectedGroup?.route_type_id ?? 0;

  const {
    interactionMode,
    isDrawingRoute,
    isEditingRoute,
    creationContext,
    startDrawingRoute,
    stopDrawingRoute,
    setRouteCreationStep,
    setRouteCreationGroupName,
    startEditingRoute,
    stopEditingRoute,
    isCalibrating,
    isPickingCalibration,
    calibrationState,
    startCalibrating,
    startRecalibrating,
    setCalibrationPoint,
    stopCalibrating,
    isEditingPath,
    startPathEditing,
    stopPathEditing,
    exitInteraction,
    resetToIdle,
  } = interaction;

  const { isTraversing, startTraversing } = traversing;

  const {
    isRouteCreationMode,
    openRouteCreationMode,
    closeRouteCreationMode,
    creationModeState: routeCreationModeState,
    isRouteDrawingActive,
    isRouteEditingActive,
    routeCreationOverlay,
    handleRouteMapClick,
    handleRouteMapMouseMove,
    handleNodeMouseDown,
    handleSegmentMouseDown,
  } = useRoutesCreationModeOrchestration({
    selectedPlan,
    routeGroupLabels,
    byGroupLabel,
    onBulkCreateRoutes,
    floorPlanPanelHandlers,
    interactionMode,
    isDrawingRoute,
    isEditingRoute,
    creationContext,
    startDrawingRoute,
    stopDrawingRoute,
    setRouteCreationStep,
    setRouteCreationGroupName,
    startEditingRoute,
    stopEditingRoute,
    setSelectedGroupLabel,
    setSelectedRouteId,
  });

  // `cable_type` on a route is its owning group label — set at creation
  // (`buildScaledRoute`) and kept in sync by `moveRouteBetweenGroups`.
  const selectedRouteGroupLabel = selectedRoute?.cable_type ?? null;

  const pageMetersPerPdfPoint = useMemo(() => {
    if (!selectedPlan || !selectedRoute) return 0;
    return (
      selectedPlan.metersPerPdfPointByPageIndex.get(selectedRoute.page) ?? 0
    );
  }, [selectedPlan, selectedRoute]);

  const {
    pathEditingOverlay,
    isPathEditingDirty,
    isPathEditingSubmitting,
    pathEditingMeters,
    submitPathEditing,
    cancelPathEditing,
    handlePathEditingMapMouseMove,
  } = useRoutePathEditing({
    floorPlanId,
    groupLabel: selectedRouteGroupLabel,
    selectedRoute,
    pageMetersPerPdfPoint,
    isPathEditing: isEditingPath,
    stopPathEditing,
    onUpdateRouteGeometry,
    floorPlanPanelHandlers,
  });

  const { seenIds, markSeenAndCheckCompletion } = useRouteTraversalModeV3({
    canEdit,
    currentRouteTypeId,
    selectedGroupRoutes,
    isCableGroupApproved,
    toggleCableGroupApproval,
  });

  const focusRoute = useCallback((route: ScaledRoute | null) => {
    setSelectedRouteId(route?.id ?? null);
  }, []);

  const handleSelectAllRoutes = useCallback(() => {
    setSelectedGroupLabel(ALL_ROUTES_GROUP_LABEL);
    const firstRoute = allPlanRoutes[0] ?? null;
    focusRoute(firstRoute);
    floorPlanPanelHandlers?.current?.zoomToFit?.();
    resetToIdle();
  }, [allPlanRoutes, focusRoute, floorPlanPanelHandlers, resetToIdle]);

  const handleSelectGroup = useCallback(
    (groupLabel: string) => {
      setSelectedGroupLabel(groupLabel);
      const firstRoute = byGroupLabel[groupLabel]?.routes[0] ?? null;
      focusRoute(firstRoute);
      floorPlanPanelHandlers?.current?.zoomToFit?.();
      resetToIdle();
    },
    [byGroupLabel, focusRoute, floorPlanPanelHandlers, resetToIdle],
  );

  const handleJumpToSelectedRouteGroup = useCallback(() => {
    if (!selectedRoute) return;
    // Open the selected route's own group, behaving exactly like picking that
    // group from the list (exits traversal via `resetToIdle`, so no half-loaded
    // "seen" progress is shown).
    handleSelectGroup(selectedRoute.cable_type);
  }, [selectedRoute, handleSelectGroup]);

  const trackRouteSelected = useCallback(
    (route: ScaledRoute) => {
      if (route.id === selectedRouteId) return;
      const routeTypeId = byGroupLabel[route.cable_type]?.route_type_id;
      if (routeTypeId === undefined) return;
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ROUTE_SELECTED, {
        routeTypeId: String(routeTypeId),
      });
    },
    [selectedRouteId, byGroupLabel],
  );

  const handleSelectRoute = useCallback(
    (routeId: string) => {
      const route =
        selectedGroupRoutes.find((item) => item.id === routeId) ?? null;
      if (route) trackRouteSelected(route);
      focusRoute(route);
      if (isTraversing && !isAllRoutesMode) {
        markSeenAndCheckCompletion(routeId);
      }
      exitInteraction();
    },
    [
      selectedGroupRoutes,
      trackRouteSelected,
      focusRoute,
      isTraversing,
      isAllRoutesMode,
      markSeenAndCheckCompletion,
      exitInteraction,
    ],
  );

  const startRouteTraversingAt = useCallback(
    (routeId: string) => {
      const route =
        selectedGroupRoutes.find((item) => item.id === routeId) ?? null;
      if (!route) return;
      trackRouteSelected(route);
      startTraversing();
      focusRoute(route);
      if (!isAllRoutesMode) {
        markSeenAndCheckCompletion(routeId);
      }
    },
    [
      selectedGroupRoutes,
      trackRouteSelected,
      startTraversing,
      focusRoute,
      isAllRoutesMode,
      markSeenAndCheckCompletion,
    ],
  );

  const selectPlanRoute = useCallback(
    (routeId: string) => {
      if (!isTraversing) {
        startRouteTraversingAt(routeId);
        return;
      }
      handleSelectRoute(routeId);
    },
    [isTraversing, startRouteTraversingAt, handleSelectRoute],
  );

  const selectFirstOfGroups = useCallback((groups: PlanRouteGroupV3[]) => {
    const first = groups[0];
    if (!first) {
      setSelectedGroupLabel(null);
      setSelectedRouteId(null);
      return;
    }
    setSelectedGroupLabel(first.label);
    setSelectedRouteId(first.routes[0]?.id ?? null);
  }, []);

  const handleStartTraversing = useCallback(() => {
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_TRAVERSAL_STARTED, {
      groupKind: "routes",
    });
    startTraversing();
    const firstRoute = selectedGroupRoutes[0];
    if (firstRoute) {
      focusRoute(firstRoute);
      if (!isAllRoutesMode) {
        markSeenAndCheckCompletion(firstRoute.id);
      }
    }
  }, [
    startTraversing,
    selectedGroupRoutes,
    focusRoute,
    isAllRoutesMode,
    markSeenAndCheckCompletion,
  ]);

  const handleDeleteRoute = useCallback(
    async (routeId: string) => {
      if (!selectedGroupLabel || !selectedPlan) return;

      const actualGroupLabel = isAllRoutesMode
        ? findRouteGroupByRouteId({ routeGroups, routeId })?.label
        : selectedGroupLabel;
      if (!actualGroupLabel) return;

      const groupRoutes = byGroupLabel[actualGroupLabel]?.routes ?? [];
      const isLastInGroup = groupRoutes.length === 1;

      try {
        await api.deleteRoute(parseInt(routeId, 10));
        const routeTypeId = byGroupLabel[actualGroupLabel]?.route_type_id;
        if (routeTypeId !== undefined) {
          trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ROUTE_DELETED, {
            routeTypeId: String(routeTypeId),
          });
        }

        onDeleteRoute({
          floorPlanId: selectedPlan.floorPlanId,
          groupLabel: actualGroupLabel,
          routeId,
        });

        const routes = isAllRoutesMode ? selectedGroupRoutes : groupRoutes;
        const currentIndex = routes.findIndex((route) => route.id === routeId);
        const nextRoute =
          routes[currentIndex + 1] ?? routes[currentIndex - 1] ?? null;

        if (isAllRoutesMode) {
          focusRoute(nextRoute);
        } else if (isLastInGroup) {
          resetToIdle();
          const nextGroupLabel =
            routeGroupLabels.find((label) => label !== actualGroupLabel) ??
            null;
          setSelectedGroupLabel(nextGroupLabel);
          const firstRoute = nextGroupLabel
            ? (byGroupLabel[nextGroupLabel]?.routes[0] ?? null)
            : null;
          focusRoute(firstRoute);
        } else {
          focusRoute(nextRoute);
          if (nextRoute && isTraversing && currentRouteTypeId) {
            markSeenAndCheckCompletion(nextRoute.id, groupRoutes.length - 1);
          }
        }
      } catch {
        toast.error(t("smartSurveyor.routeInspector.deleteFailed"));
      }
    },
    [
      selectedGroupLabel,
      selectedPlan,
      isAllRoutesMode,
      routeGroups,
      byGroupLabel,
      routeGroupLabels,
      selectedGroupRoutes,
      onDeleteRoute,
      resetToIdle,
      focusRoute,
      isTraversing,
      currentRouteTypeId,
      markSeenAndCheckCompletion,
      t,
    ],
  );

  const commitRouteRename = useCallback(
    async ({
      oldGroupLabel,
      newGroupLabel,
      routeTypeId,
      applyToProject,
    }: CommitRouteRenameArgs) => {
      try {
        const { affectedPlans } = await api.renameRouteType(
          routeTypeId,
          newGroupLabel,
          applyToProject,
        );
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_RENAMED, {
          groupKind: "routes",
          scope: applyToProject ? "project" : "plan",
        });

        onRenameRouteGroups({ oldGroupLabel, newGroupLabel, affectedPlans });
        setSelectedGroupLabel(newGroupLabel);
      } catch {
        toast.error(t("smartSurveyor.groupInspector.renameFailed"));
      }
    },
    [onRenameRouteGroups, setSelectedGroupLabel, t],
  );

  const renameRouteGroupsFromOverview: RenameRouteGroups = useCallback(
    ({ oldGroupLabel, newGroupLabel, affectedPlans }) => {
      onRenameRouteGroups({ oldGroupLabel, newGroupLabel, affectedPlans });
      if (!selectedPlan || selectedGroupLabel !== oldGroupLabel) return;
      // A rename on other plans leaves this plan's group under its old name
      const isRenamedOnSelectedPlan = affectedPlans.some(
        (affectedPlan) => affectedPlan.floorPlanId === selectedPlan.floorPlanId,
      );
      if (!isRenamedOnSelectedPlan) return;
      setSelectedGroupLabel(newGroupLabel);
    },
    [
      onRenameRouteGroups,
      selectedPlan,
      selectedGroupLabel,
      setSelectedGroupLabel,
    ],
  );

  const handleRenameRouteGroup = useCallback(
    async (newGroupLabel: string) => {
      if (!selectedGroupLabel || !selectedPlan || isAllRoutesMode) return;
      const routeTypeId = selectedGroup?.route_type_id;
      if (!routeTypeId) return;

      const renameArgs: CommitRouteRenameArgs = {
        oldGroupLabel: selectedGroupLabel,
        newGroupLabel,
        routeTypeId,
        applyToProject: false,
      };

      // A placeholder name collides with unrelated groups on other plans, so never fan out.
      if (isPlaceholderGroupName(selectedGroupLabel)) {
        await commitRouteRename(renameArgs);
        return;
      }

      try {
        const preview = await api.getQuantitySurveyorRouteTypeRenamePreview(
          routeTypeId,
          newGroupLabel,
        );

        if (preview.plans.length > 0) {
          setPendingRouteRename({ ...renameArgs, plans: preview.plans });
          return;
        }
      } catch {
        // A failed preview shouldn't block the rename; fall through to the current plan only.
      }

      await commitRouteRename(renameArgs);
    },
    [
      isAllRoutesMode,
      selectedGroupLabel,
      selectedPlan,
      selectedGroup,
      commitRouteRename,
    ],
  );

  const confirmRouteRenameForAllPlans = useCallback(async () => {
    if (!pendingRouteRename) return;
    setRouteRenameSubmitAction("allPlans");
    await commitRouteRename({ ...pendingRouteRename, applyToProject: true });
    setRouteRenameSubmitAction(null);
    setPendingRouteRename(null);
  }, [pendingRouteRename, commitRouteRename]);

  const confirmRouteRenameForCurrentPlan = useCallback(async () => {
    if (!pendingRouteRename) return;
    setRouteRenameSubmitAction("currentPlan");
    await commitRouteRename({ ...pendingRouteRename, applyToProject: false });
    setRouteRenameSubmitAction(null);
    setPendingRouteRename(null);
  }, [pendingRouteRename, commitRouteRename]);

  const cancelPendingRouteRename = useCallback(() => {
    setPendingRouteRename(null);
  }, []);

  const selectNextRouteGroupAfter = useCallback(
    (emptiedGroupLabel: string) => {
      const nextGroupLabel =
        routeGroupLabels.find((label) => label !== emptiedGroupLabel) ?? null;
      if (nextGroupLabel) {
        handleSelectGroup(nextGroupLabel);
        return;
      }
      resetToIdle();
      setSelectedGroupLabel(null);
      focusRoute(null);
    },
    [routeGroupLabels, handleSelectGroup, resetToIdle, focusRoute],
  );

  // An undo does not go through the handlers here, so a selected cable group it renamed or removed is followed here.
  const followHistoryChanges = useCallback(
    ({
      floorPlanId: changedFloorPlanId,
      changes,
    }: FollowHistoryChangesArgs) => {
      if (floorPlanId !== changedFloorPlanId) return;
      const next = selectedRouteGroupAfterHistory({
        selectedGroupLabel,
        changes,
        labelOfTypeId: (typeId) =>
          routeGroups.find((group) => group.route_type_id === typeId)?.label,
      });
      if (next.kind === "select") {
        setSelectedGroupLabel(next.groupLabel);
      } else if (next.kind === "selectNext" && selectedGroupLabel !== null) {
        selectNextRouteGroupAfter(selectedGroupLabel);
      }
    },
    [floorPlanId, selectedGroupLabel, routeGroups, selectNextRouteGroupAfter],
  );

  const handleDeleteRouteGroup = useCallback(async () => {
    if (!selectedGroupLabel || !selectedPlan || isAllRoutesMode) return;
    if (!selectedGroup?.route_type_id) return;
    const routeTypeId = selectedGroup.route_type_id;

    try {
      await api.deleteQuantitySurveyorRouteType(routeTypeId);
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_DELETED, {
        groupKind: "routes",
        groupTypeId: String(routeTypeId),
        elementCount: selectedGroup.routes.length,
      });
      onDeleteRouteGroup({
        floorPlanId: selectedPlan.floorPlanId,
        groupLabel: selectedGroupLabel,
      });

      selectNextRouteGroupAfter(selectedGroupLabel);
    } catch {
      toast.error(t("smartSurveyor.groupInspector.deleteGroupFailed"));
    }
  }, [
    isAllRoutesMode,
    selectedGroupLabel,
    selectedPlan,
    selectedGroup,
    onDeleteRouteGroup,
    selectNextRouteGroupAfter,
    t,
  ]);

  const handleMarkReviewed = useCallback(async () => {
    if (!selectedGroupLabel || !selectedPlan || isAllRoutesMode) return;
    const routeTypeId = selectedGroup?.route_type_id;
    if (!routeTypeId) return;
    await api.updateQuantitySurveyorRouteType(routeTypeId, {
      cableTypeStatus: "confident",
    });
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_MARKED_REVIEWED, {
      groupKind: "routes",
      groupTypeId: String(routeTypeId),
    });
    onMarkRouteTypeReviewed({
      floorPlanId: selectedPlan.floorPlanId,
      routeTypeIds: [routeTypeId],
    });
  }, [
    isAllRoutesMode,
    selectedGroupLabel,
    selectedPlan,
    selectedGroup,
    onMarkRouteTypeReviewed,
  ]);

  const handleSetCableTypeStatus = useCallback(
    async (cableTypeStatus: ConfidenceLevel) => {
      if (!selectedGroupLabel || !selectedPlan || isAllRoutesMode) return;
      const routeTypeId = selectedGroup?.route_type_id;
      if (!routeTypeId) return;
      await api.updateQuantitySurveyorRouteType(routeTypeId, {
        cableTypeStatus,
      });
      onSetCableTypeNameStatus({
        floorPlanId: selectedPlan.floorPlanId,
        routeTypeIds: [routeTypeId],
        cableTypeStatus,
      });
    },
    [
      isAllRoutesMode,
      selectedGroupLabel,
      selectedPlan,
      selectedGroup,
      onSetCableTypeNameStatus,
    ],
  );

  const handleSaveLvMatch = useCallback(
    async (lvMatch: LvMatch) => {
      if (!selectedPlan || !selectedRoute) return;

      const targetGroup = isAllRoutesMode
        ? findRouteGroupByRouteId({ routeGroups, routeId: selectedRoute.id })
        : selectedGroup;

      if (!targetGroup) return;

      try {
        await api.updateQuantitySurveyorRouteType(targetGroup.route_type_id, {
          lvMatchId: lvMatch.lvId ?? null,
          lvMatchName: lvMatch.lvName ?? null,
          lvMatchReasoning: lvMatch.reasoning ?? null,
        });
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_LV_MATCH_SAVED, {
          groupKind: "routes",
          groupTypeId: String(targetGroup.route_type_id),
        });
        onSetRouteGroupLvMatch({
          floorPlanId: selectedPlan.floorPlanId,
          floorPlanRouteGroupLabel: targetGroup.label,
          lvMatch,
        });
      } catch {
        toast.error(t("smartSurveyor.lvMatchEditor.saveFailed"));
        throw new Error("Failed to save BOQ position");
      }
    },
    [
      isAllRoutesMode,
      selectedPlan,
      routeGroups,
      selectedRoute,
      selectedGroup,
      onSetRouteGroupLvMatch,
      t,
    ],
  );

  const handleChangeRouteType = useCallback(
    async (routeId: string, newCableType: string) => {
      if (!selectedGroupLabel || !selectedPlan) return;

      const actualGroupLabel = isAllRoutesMode
        ? findRouteGroupByRouteId({ routeGroups, routeId })?.label
        : selectedGroupLabel;
      if (!actualGroupLabel) return;

      const groupChanged = newCableType !== actualGroupLabel;
      if (!groupChanged) return;

      const groupRoutes = byGroupLabel[actualGroupLabel]?.routes ?? [];
      const isLastInGroup = groupRoutes.length === 1;

      try {
        const response = await api.updateRoute(parseInt(routeId, 10), {
          routeTypeName: newCableType,
        });
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ROUTE_TYPE_CHANGED);

        onUpdateRoute({
          floorPlanId: selectedPlan.floorPlanId,
          routeId,
          fromGroupLabel: actualGroupLabel,
          toGroupLabel: newCableType,
          newRouteTypeId: response.routeTypeId,
        });

        if (isAllRoutesMode) {
          return;
        }

        if (isLastInGroup) {
          setSelectedGroupLabel(newCableType);
        } else {
          const remainingRoutes = groupRoutes.filter(
            (route) => route.id !== routeId,
          );
          focusRoute(remainingRoutes[0] ?? null);
        }
      } catch {
        toast.error(t("smartSurveyor.routeInspector.changeTypeFailed"));
      }
    },
    [
      selectedGroupLabel,
      selectedPlan,
      isAllRoutesMode,
      routeGroups,
      byGroupLabel,
      onUpdateRoute,
      focusRoute,
      setSelectedGroupLabel,
      t,
    ],
  );

  useEffect(() => {
    if (floorPlanId === prevFloorPlanIdRef.current) return;
    prevFloorPlanIdRef.current = floorPlanId;
    resetToIdle();
    setSelectedGroupLabel(ALL_ROUTES_GROUP_LABEL);
    setSelectedRouteId(routeGroups[0]?.routes[0]?.id ?? null);
  }, [floorPlanId, routeGroups, resetToIdle]);

  return {
    planRouteTypeIds,
    totalCableMeters,
    routeGroupLabels,
    byGroupLabel,
    allPlanRoutes,
    isAllRoutesMode,
    selectedGroupLabel,
    selectedGroup,
    selectedGroupRoutes,
    selectedRoute,
    followHistoryChanges,
    totalSelectedGroupRoutes,
    handleSelectAllRoutes,
    handleSelectGroup,
    handleJumpToSelectedRouteGroup,
    handleSelectRoute,
    selectPlanRoute,
    handleDeleteRoute,
    handleDeleteRouteGroup,
    handleRenameRouteGroup,
    renameRouteGroupsFromOverview,
    pendingRouteRename,
    routeRenameSubmitAction,
    confirmRouteRenameForAllPlans,
    confirmRouteRenameForCurrentPlan,
    cancelPendingRouteRename,
    handleMarkReviewed,
    handleSetCableTypeStatus,
    handleSaveLvMatch,
    handleChangeRouteType,
    selectFirstOfGroups,
    handleStartTraversing,
    resetToIdle,
    seenIds,
    focusRoute,
    isRouteCreationMode,
    openRouteCreationMode,
    closeRouteCreationMode,
    routeCreationModeState,
    isRouteDrawingActive,
    isRouteEditingActive,
    routeCreationOverlay,
    handleRouteMapClick,
    handleRouteMapMouseMove,
    handleNodeMouseDown,
    handleSegmentMouseDown,
    isCalibrating,
    isPickingCalibration,
    calibrationState,
    startCalibrating,
    startRecalibrating,
    setCalibrationPoint,
    stopCalibrating,
    isPathEditing: isEditingPath,
    pathEditingOverlay,
    isPathEditingDirty,
    isPathEditingSubmitting,
    pathEditingMeters,
    startPathEditing,
    submitPathEditing,
    cancelPathEditing,
    handlePathEditingMapMouseMove,
  } as const;
};
